import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import PageMeta from "../seo/PageMeta";
import { useNavigate, useParams } from "react-router-dom";
import { collection, getDocs, limit, orderBy, query, where, doc, getDoc, updateDoc } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../firebase";
import Menu from "../sections/Menu";
import Footer from "../sections/Footer";

const allowedEmails = [
  "iwona.bendig@egodziny.pl",
  "krzysztof.warda@egodziny.pl",
  "dominik.pogorzelski@egodziny.pl",
  "wiktor.spryszynski@egodziny.pl",
];

const formatDateTime = (value) => {
  if (!value) return "-";
  if (typeof value === "string") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString("pl-PL");
  }
  if (value.toDate) {
    return value.toDate().toLocaleString("pl-PL");
  }
  return "-";
};

const formatValue = (value) => {
  if (typeof value === "boolean") return value ? "Tak" : "Nie";
  if (value === null || typeof value === "undefined") return "-";
  return String(value);
};

const labelField = (field) => {
  const labels = {
    name: "Imię",
    surname: "Nazwisko",
    email: "Email",
    phone: "Telefon",
    companyName: "Nazwa firmy",
    companyAddress: "Adres firmy",
    nip: "NIP",
    position: "Stanowisko",
    acceptedTerms: "Akceptacja regulaminu",
    companyType: "Typ firmy",
    timeTrackingMethod: "Metoda rejestracji czasu",
    gpsMode: "Tryb GPS",
    registrationMethod: "Metoda rejestracji",
    scheduleUsage: "Grafik",
    captchaToken: "Captcha",
  };
  return labels[field] || field;
};

const formatEventDetails = (event) => {
  if (!event || !event.type) return "-";

  const stepLabel = event.step ? `Krok ${event.step}` : null;

  switch (event.type) {
    case "session_start":
      return "Rozpoczęcie sesji";
    case "step_view":
      return stepLabel ? `Wyświetlenie ${stepLabel}` : "Wyświetlenie kroku";
    case "step_submit_attempt":
      return stepLabel
        ? `Próba przejścia dalej (${stepLabel})${event.is_final ? " - finalny krok" : ""}`
        : "Próba przejścia dalej";
    case "step_snapshot": {
      const data = event.data || {};
      const entries = Object.entries(data)
        .filter(([, value]) => value !== "" && value !== null && typeof value !== "undefined")
        .map(([key, value]) => `${labelField(key)}: ${formatValue(value)}`);
      if (entries.length === 0) return stepLabel ? `Podsumowanie ${stepLabel}` : "Podsumowanie kroku";
      return `${stepLabel ? `${stepLabel} - ` : ""}${entries.join(", ")}`;
    }
    case "field_update":
      return `${stepLabel ? `${stepLabel} - ` : ""}Wpisano ${labelField(event.field)}: ${formatValue(event.value)}`;
    case "selection_update":
      return `${stepLabel ? `${stepLabel} - ` : ""}Wybrano ${labelField(event.field)}: ${formatValue(event.value)}`;
    case "phone_confirmed":
      return "Telefon potwierdzony";
    case "sms_redirect":
      return "Przekierowanie do weryfikacji SMS";
    case "sms_redirect_resend":
      return "Przekierowanie do weryfikacji SMS (ponowna wysyłka)";
    case "phone_modal_shown":
      return "Modal telefonu wyświetlony";
    case "phone_modal_cancelled":
      return "Modal telefonu anulowany";
    case "captcha_shown":
      return "Captcha wyświetlona";
    case "captcha_resolved":
      return "Captcha potwierdzona";
    case "captcha_expired":
      return "Captcha wygasła";
    case "captcha_closed":
      return "Captcha zamknięta (Anuluj)";
    case "submit_started":
      return "Wysyłka formularza rozpoczęta (wywołanie API)";
    case "submit_success":
      return "Formularz wysłany";
    case "submit_validation_failed":
      return `Błąd walidacji${stepLabel ? ` (${stepLabel})` : ""}`;
    case "submit_error":
      return "Błąd wysyłki formularza";
    case "submit_exception":
      return "Wyjątek podczas wysyłki";
    default:
      return event.type;
  }
};

export default function AdminPanelSession() {
  const { sessionId } = useParams();
  const [events, setEvents] = useState([]);
  const [isFlagged, setIsFlagged] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [newerSessionId, setNewerSessionId] = useState(null);
  const [olderSessionId, setOlderSessionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [expandedEvents, setExpandedEvents] = useState(new Set());
  const [sortOrder, setSortOrder] = useState("desc");
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        navigate(`/login/blog?redirect=/admin-panel/${sessionId}`);
        return;
      }
      if (!allowedEmails.includes(currentUser.email)) {
        alert("Brak dostępu");
        navigate("/");
        return;
      }
      setIsAuthorized(true);
    });
    return () => unsubscribe();
  }, [auth, navigate, sessionId]);

  useEffect(() => {
    const loadEvents = async () => {
      const sessionSnap = await getDoc(doc(db, "registration_sessions", sessionId));
      const sessionData = sessionSnap.exists() ? sessionSnap.data() : null;
      const sessionCreatedAt = sessionData?.createdAt || null;

      const sessionsRef = collection(db, "registration_sessions");
      const [eventsSnapshot, newerSnap, olderSnap] = await Promise.all([
        getDocs(
          query(
            collection(db, "registration_sessions", sessionId, "events"),
            orderBy("createdAt", sortOrder),
            limit(500)
          )
        ),
        sessionCreatedAt
          ? getDocs(query(sessionsRef, where("createdAt", ">", sessionCreatedAt), orderBy("createdAt", "asc"), limit(1)))
          : Promise.resolve(null),
        sessionCreatedAt
          ? getDocs(query(sessionsRef, where("createdAt", "<", sessionCreatedAt), orderBy("createdAt", "desc"), limit(1)))
          : Promise.resolve(null),
      ]);

      const rows = eventsSnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      setEvents(rows);
      setIsFlagged(!!sessionData?.isFlagged);
      setIsEmpty(!!sessionData?.isEmpty);
      setNewerSessionId(newerSnap && !newerSnap.empty ? newerSnap.docs[0].id : null);
      setOlderSessionId(olderSnap && !olderSnap.empty ? olderSnap.docs[0].id : null);
      setLoading(false);
    };

    if (sessionId && isAuthorized) {
      loadEvents();
    }
  }, [sessionId, isAuthorized, sortOrder]);

  const toggleFlag = async () => {
    const newValue = !isFlagged;
    try {
      await updateDoc(doc(db, "registration_sessions", sessionId), { isFlagged: newValue });
      setIsFlagged(newValue);
    } catch (error) {
      console.error("Nie udało się zmienić flagi sesji.", error);
    }
  };

  const toggleEmpty = async () => {
    const newValue = !isEmpty;
    try {
      await updateDoc(doc(db, "registration_sessions", sessionId), { isEmpty: newValue });
      setIsEmpty(newValue);
    } catch (error) {
      console.error("Nie udało się zmienić flagi pustego formularza.", error);
    }
  };

  const toggleEventDetails = (eventId) => {
    setExpandedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  return (
    <>
      <PageMeta path="/admin-panel/:sessionId" />
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      <Menu />
      <main style={{ padding: "2rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <h1 style={{ margin: 0, display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            Eventy: {sessionId}
            {isFlagged && (
              <span style={{ fontSize: "14px", fontWeight: "normal", background: "#fff8e1", border: "1px solid #ffe082", borderRadius: "4px", padding: "2px 8px" }}>
                wewnętrzne
              </span>
            )}
            {isEmpty && (
              <span style={{ fontSize: "14px", fontWeight: "normal", background: "#f0f0f0", border: "1px solid #ccc", borderRadius: "4px", padding: "2px 8px" }}>
                pusty formularz
              </span>
            )}
          </h1>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              Sortowanie:
              <select
                className="login-input"
                value={sortOrder}
                onChange={(event) => setSortOrder(event.target.value)}
                style={{ maxWidth: "220px" }}
              >
                <option value="desc">Od najnowszych</option>
                <option value="asc">Od najstarszych</option>
              </select>
            </label>
            <button
              className="button-default btn"
              style={isFlagged ? { background: "#e0e0e0", color: "#333" } : undefined}
              onClick={toggleFlag}
            >
              {isFlagged ? "Odflaguj" : "Oflaguj"}
            </button>
            <button
              className="button-default btn"
              style={isEmpty ? { background: "#d0d0d0", color: "#333" } : undefined}
              onClick={toggleEmpty}
            >
              {isEmpty ? "Oznacz niepusty" : "Oznacz pusty"}
            </button>
            <button
              className="button-default btn"
              disabled={!newerSessionId}
              onClick={() => newerSessionId && navigate(`/admin-panel/${newerSessionId}`)}
              title="Nowszy"
            >
              ←
            </button>
            <button
              className="button-default btn"
              disabled={!olderSessionId}
              onClick={() => olderSessionId && navigate(`/admin-panel/${olderSessionId}`)}
              title="Starszy"
            >
              →
            </button>
            <button className="button-default btn" onClick={() => navigate("/admin-panel")}>
              Wróć
            </button>
          </div>
        </div>
        {loading ? (
          <p>Ładowanie...</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>Zdarzenie</th>
                  <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>Czas</th>
                  <th style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #ddd" }}>Dane</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.id}>
                    <td style={{ padding: "10px", borderBottom: "1px solid #eee" }}>
                      {formatEventDetails(event)}
                    </td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #eee" }}>
                      {formatDateTime(event.timestamp || event.createdAt)}
                    </td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #eee" }}>
                      <button
                        className="button-default btn"
                        onClick={() => toggleEventDetails(event.id)}
                        style={{ padding: "6px 12px", fontSize: "12px" }}
                      >
                        {expandedEvents.has(event.id) ? "Ukryj" : "Pokaż"}
                      </button>
                      {expandedEvents.has(event.id) && (
                        <pre style={{ marginTop: "8px", whiteSpace: "pre-wrap" }}>
                          {event.type ? JSON.stringify(event, null, 2) : "-"}
                        </pre>
                      )}
                    </td>
                  </tr>
                ))}
                {events.length === 0 && (
                  <tr>
                    <td colSpan={3} style={{ padding: "10px" }}>
                      Brak danych.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}
